import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import MoreIcon from '@material-ui/icons/MoreVert';
import { Alert, AlertTitle } from '@material-ui/lab';
import { observer } from 'mobx-react-lite';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import React, { useState } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import LoadingButton from '../../../common/LoadingButton';
import WorkflowBuilderTab from './WorkflowBuilderTab';
import WorkflowJsonTab from './WorkflowJsonTab';
import WorkflowUploadDialog from './WorkflowUploadDialog';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    header: {
      marginBottom: 16,
      [theme.breakpoints.up('md')]: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }
    },
    titleContainer: {
      marginBottom: 24,
      [theme.breakpoints.up('md')]: {
        marginBottom: 0
      }
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03),
      marginBottom: 24
    },
    cancelButton: {
      marginRight: 16
    },
    alert: {
      marginBottom: 24
    },
    moreActionsButton: { 
      marginLeft: 8 
    }
  })
);

interface Props {
  title: string;
  workflow: any;
  famlily: any;
  tests: any[];
  cspGroups: any;
  environments: any;
  showFileUpload: boolean;
  onWorkflowChange: (workflow: any) => void;
  onSave: () => void;
  onSaveComplete: (workflow: any) => void;
  onCancel: () => void;
}

function WorkflowForm(props: Props & RouteComponentProps<any> & WithSnackbarProps) {
  const {
    title,
    workflow,
    family,
    tests,
    cspGroups,
    environments,
    showFileUpload,
    onWorkflowChange,
    onSave,
    onSaveComplete,
    onCancel
  } = props;

  const [showErrors, setShowErrors] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [showFileUploadDialog, setShowFileUploadDialog] = useState(!!showFileUpload);
  const [uploadCount, setUploadCount] = useState(0);
  const [tab, setTab] = useState('builder');

  const classes = useStyles({});

  const onTabChange = (event: React.ChangeEvent<{}>, newValue: string) => {
    setTab(newValue)
  };

  const onLoadFromFile = () => {
    setMenuAnchorEl(null);
    setShowFileUploadDialog(true);
  };

  const onOpenMoreMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setMenuAnchorEl(event.currentTarget);
  };

  const onCloseWorkflowUploadDialog = (newWorkflow?: any) => {
    setShowFileUploadDialog(false);
    if (newWorkflow) {
      onWorkflowChange(newWorkflow);
      setUploadCount(prevUploadCount => prevUploadCount + 1);
    }
  };

  const onSaveWrapper = (event) => {
    setSaveInProgress(true);
    setValidationError(null);
    // Check for validation errors
    onSave().then((savedWorkflow) => {
      setSaveInProgress(false);
      onSaveComplete(savedWorkflow);
    }).catch(error => {
      setSaveInProgress(false);
      setShowErrors(true);
      if (error.status === 400) {
        setValidationError(error.data.errors ? error.data.errors : [error.data.errorMessage]);
      } else if (error.status === 403) {
        setValidationError([error.data.errorMessage]);
      } else {
        console.log(error);
        setValidationError(['Oops, something went wrong... please try again']);
      }
    });
  };

  return (
    <div>
      <div className={classes.header}>
        <div className={classes.titleContainer}>
          <Typography variant="h6">{title}</Typography>
        </div>
        <div>
          <Button color="primary" variant="outlined" onClick={onCancel} className={classes.cancelButton}>Cancel</Button>
          <LoadingButton
            color="primary"
            loading={saveInProgress}
            variant="contained"
            onClick={onSaveWrapper}>
            Save
          </LoadingButton>
          <Tooltip title="More Actions">
            <IconButton className={classes.moreActionsButton} onClick={onOpenMoreMenu}><MoreIcon /></IconButton>
          </Tooltip>
          <Menu
            id="more-options-menu"
            anchorEl={menuAnchorEl}
            keepMounted
            open={Boolean(menuAnchorEl)}
            onClose={() => setMenuAnchorEl(null)}
          >
            <MenuItem onClick={onLoadFromFile}>
              Load worfklow from file
            </MenuItem>
          </Menu>
        </div>
      </div>

      {validationError && <Alert severity="warning" className={classes.alert}>
        <AlertTitle>Failed to save workflow</AlertTitle>
        {validationError.map((error, index) => <Typography key={index}>{error}</Typography>)}
      </Alert>}
      <Tabs
        className={classes.tabs}
        value={tab}
        onChange={onTabChange}
        aria-label="Edit Workflow Tabs"
        indicatorColor="primary"
        textColor="primary"
      >
        <Tab label="Builder" value="builder" />
        <Tab label="JSON Editor" value="json" />
      </Tabs>
      {tab === 'builder' && <WorkflowBuilderTab
        workflow={workflow}
        family={family}
        tests={tests}
        showErrors={showErrors}
        groups={cspGroups}
        environments={environments}
        onWorkflowChange={onWorkflowChange}
        key={`workflowBuilderTab${uploadCount}`}
      />}
      {tab === 'json' && <WorkflowJsonTab
        workflow={workflow}
        onWorkflowChange={onWorkflowChange}
        key={`workflowJsonTab${uploadCount}`}
      />}
      {showFileUploadDialog && <WorkflowUploadDialog familyId={family} onClose={onCloseWorkflowUploadDialog} />}
    </div>
  );
};

export default withRouter(withSnackbar(observer(WorkflowForm)));
